import { D, roundFinal } from './money';

// A selection is the sold quantity as a decimal string by modulus ("0" = none).
// Lots are addressed by 0-based fifoOrder; SHORT positions work by modulus.

const total = (pos) => D(pos.quantity).abs();
const lotQty = (lot) => D(lot.quantity).abs();

// «До продажу» takes a fraction on every position, to two decimals; a position held finer keeps its own precision
// (owner's decision of 2026-10-29, AC-21).
const MIN_SALE_SCALE = 2;
const saleScale = (pos) => Math.max(MIN_SALE_SCALE, pos.quantityScale);

const fail = (pos, reason) => ({
  ok: false,
  reason,
  min: '0',
  max: total(pos).toFixed(),
  scale: saleScale(pos),
});

const NUMBER_RE = /^[+-]?(\d+(\.\d*)?|\.\d+)$/;

const hasPackageData = (lot) =>
  [lot.openValueUah, lot.closeValueUah, lot.openPrice, lot.openExchangeRate].every(
    (v) => v !== undefined && v !== null,
  );

// Sale result in UAH per sold lot, the way the declaration's FIFO packaging books it
// (taxer: ConvertCurrenciesServiceImpl, DivideFIFOTradeServiceImpl.allocate, FifoPackageDto.financialResult):
// the whole sale is converted to kopecks once, then split between packages by quantity, and a partly
// sold lot splits its amount and commission separately, each rounded to kopecks (HALF_UP).
// Without the packaging fields (not a real snapshot) it falls back to the exact proportion.
const lotResultsUah = (pos, sold) => {
  const proportional = ({ lot, part, of }) => D(lot.unrealizedResultUah).times(part).div(of);
  if (!sold.every(({ lot }) => hasPackageData(lot))) return sold.map(proportional);
  const sign = pos.side === 'SHORT' ? -1 : 1;
  let restQty = sold.reduce((acc, { part }) => acc.plus(part), D(0));
  let restSale = roundFinal(
    sold.reduce((acc, { lot, part, of }) => acc.plus(D(lot.closeValueUah).times(part).div(of)), D(0)),
  );
  return sold.map(({ lot, part, of }) => {
    const salePart = part.lt(restQty) ? roundFinal(restSale.times(part).div(restQty)) : restSale;
    restSale = restSale.minus(salePart);
    restQty = restQty.minus(part);
    let openPart = D(lot.openValueUah);
    if (part.lt(of)) {
      const amount = roundFinal(D(lot.openPrice).times(of), { scale: 10, mode: 'HALF_UP' }).times(lot.openExchangeRate);
      const amountUah = roundFinal(amount);
      const commissionUah = openPart.minus(amountUah);
      openPart = roundFinal(amountUah.times(part).div(of)).plus(roundFinal(commissionUah.times(part).div(of)));
    }
    return salePart.minus(openPart).times(sign);
  });
};

const lotsFor = (pos, quantity) => {
  let rest = D(quantity);
  const lots = [];
  const sold = [];
  let result = D(0);
  pos.lots.forEach((lot) => {
    if (rest.lte(0)) return;
    const of = lotQty(lot);
    const part = rest.lt(of) ? rest : of;
    rest = rest.minus(part);
    result = result.plus(D(lot.unrealizedResult).times(part).div(of));
    sold.push({ lot, part, of });
    lots.push({ fifoOrder: lot.fifoOrder, sold: part.toFixed(), of: of.toFixed() });
  });
  const byLot = lotResultsUah(pos, sold);
  return {
    lots,
    result: result.toFixed(),
    resultUah: byLot.reduce((acc, v) => acc.plus(v), D(0)).toFixed(),
    lotResultsUah: byLot.map((v) => v.toFixed()),
  };
};

const success = (pos, quantity, extra = {}) => ({
  ok: true,
  quantity: D(quantity).toFixed(),
  ...lotsFor(pos, quantity),
  ...extra,
});

// What the investor typed into «До продажу», as selectQuantity reads it: spaces (NBSP included) dropped, a decimal comma
// read as a point, an emptied field as nothing to sell (T54, B2-07).
export const normalizeQuantityInput = (input) => {
  const text = String(input ?? '').replace(/[\s\u00a0\u202f]/g, '').replace(',', '.');
  return text === '' ? '0' : text;
};

export const selectQuantity = (pos, input) => {
  if (pos.price === null) return fail(pos, 'NO_PRICE');
  const text = String(input ?? '').trim();
  if (!NUMBER_RE.test(text)) return fail(pos, 'NOT_A_NUMBER');
  const q = D(text);
  if (q.isNegative() && !q.isZero()) return fail(pos, 'NEGATIVE');
  if (q.decimalPlaces() > saleScale(pos)) return fail(pos, 'TOO_PRECISE');
  if (q.gt(total(pos))) return success(pos, total(pos), { clamped: true });
  return success(pos, q);
};

const sumLots = (pos, count) =>
  pos.lots.slice(0, count).reduce((acc, lot) => acc.plus(lotQty(lot)), D(0));

export const selectLot = (pos, n) => {
  if (pos.price === null) return fail(pos, 'NO_PRICE');
  if (!Number.isInteger(n) || n < 0 || n >= pos.lots.length) return fail(pos, 'INVALID_LOT');
  return success(pos, sumLots(pos, n + 1));
};

export const unselectLot = (pos, n) => {
  if (pos.price === null) return fail(pos, 'NO_PRICE');
  if (!Number.isInteger(n) || n < 0 || n >= pos.lots.length) return fail(pos, 'INVALID_LOT');
  return success(pos, sumLots(pos, n));
};

export const positionState = (pos, quantity) => {
  const q = D(quantity);
  if (q.lte(0)) return 'none';
  return q.gte(total(pos)) ? 'all' : 'partial';
};

export const togglePosition = (pos, currentQuantity) => {
  if (pos.price === null) return fail(pos, 'NO_PRICE');
  return success(pos, positionState(pos, currentQuantity) === 'all' ? D(0) : total(pos));
};

export const selectedLotCount = (pos, quantity) => {
  let rest = D(quantity);
  let count = 0;
  for (const lot of pos.lots) {
    const of = lotQty(lot);
    if (rest.lt(of)) break;
    rest = rest.minus(of);
    count += 1;
  }
  return count;
};
